import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';

const phoneNumber = '416-302-6360';

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isScrolled, setIsScrolled] = React.useState(false);
  const location = useLocation();

  React.useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '/', label: 'Check Your Home Equity' },
    { href: '/bungalows', label: 'Resale Bungalows' },
    { href: '/new-construction', label: 'New Builds' },
    { href: '/founders-note', label: 'Founder’s Note' },
    { href: '/trust-transparency', label: 'Trust & Transparency' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[#050914] backdrop-blur-md border-b border-[#D4AF37]/[0.22] shadow-[0_14px_42px_rgba(0,0,0,0.46)]'
          : 'bg-[#050914] backdrop-blur-md border-b border-[#D4AF37]/[0.22] shadow-[0_14px_42px_rgba(0,0,0,0.46)]'
      }`}
      data-testid="navbar"
    >
      <div className="w-full px-5 lg:px-8">
        <div className="flex items-center justify-between h-[160px]">
          {/* Logo Lockup */}
          <Link
            to="/"
            className="flex shrink-0 items-center gap-2"
            aria-label="DownsizeGTA Downsizing Advisory"
          >
            <span className="relative h-[140px] sm:h-[155px] w-[112px] sm:w-[120px] shrink-0 overflow-hidden" aria-hidden="true">
              <img
                src="/downsizegta-emblem.png"
                alt=""
                className="absolute left-1/2 top-1/2 h-[170px] sm:h-[180px] w-auto max-w-none -translate-x-1/2 -translate-y-1/2"
              />
            </span>
            <div className="hidden sm:flex flex-col">
              <span className="font-playfair text-[#D4AF37] text-[28px] font-semibold tracking-[0.01em] leading-tight">
                DownsizeGTA
              </span>
              <span className="text-white text-[10px] tracking-[0.18em] uppercase font-medium mt-1">
                Downsizing Advisory
              </span>
            </div>
          </Link>

          {/* Desktop: Nav + phone group */}
          <div className="hidden xl:flex flex-col 2xl:flex-row items-end 2xl:items-center gap-2 2xl:gap-10">
            {/* Navigation */}
            <div className="flex items-center gap-4 2xl:gap-8">
              {/* Full-page link: the flagship is a standalone Vercel page, not a React Router route. */}
              <a
                href="/private-downsizing"
                className="font-semibold text-sm tracking-wide text-[#D4AF37] hover:text-[#F0D58F] whitespace-nowrap border-b border-[#D4AF37]/60 pb-1"
              >
                Private Downsizing
              </a>
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`font-medium text-sm tracking-wide transition-colors whitespace-nowrap ${
                    location.pathname === link.href
                      ? 'text-[#D4AF37]'
                      : 'text-white hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Phone */}
            <a
              href={`tel:${phoneNumber}`}
              className="flex items-center gap-1.5 text-white hover:text-white text-xs tracking-wide transition-colors whitespace-nowrap"
            >
              <Phone size={13} className="text-white/80" />
              <span>{phoneNumber}</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="xl:hidden text-white p-2"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="xl:hidden bg-[#0B1020]/98 backdrop-blur-md border-t border-white/[0.06]">
          <div className="px-6 py-6 space-y-4">
            <a
              href="/private-downsizing"
              onClick={() => setMobileMenuOpen(false)}
              className="block font-semibold text-base text-[#D4AF37]"
            >
              Private Downsizing
            </a>
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`block font-medium text-base ${
                  location.pathname === link.href ? 'text-[#D4AF37]' : 'text-white'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 border-t border-white/[0.06]">
              <a
                href={`tel:${phoneNumber}`}
                className="flex items-center gap-2 text-white text-xs mb-4"
              >
                <Phone size={13} />
                <span>{phoneNumber}</span>
              </a>
              <Link
                to="/calculator"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-center bg-gradient-to-b from-[#D4AF37] to-[#c5a030] text-[#1C1F24] px-5 py-3.5 rounded-full font-semibold shadow-[0_2px_14px_rgba(212,175,55,0.28)]"
              >
                Begin Private DAS™ Analysis
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;